import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Raffle.module.css';
import {
    TicketType,
    Ticket,
    UpdateTicketBonusInput,
    raffleService,
    PeriodType
} from '../../services/raffle.service';
import { authService } from '../../services/auth.service';
import {
    getAllEnvironmentsService,
    Environment
} from '../../services/getAllEnvironment.service';

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getPeriodLabel = (period: string) => {
    const periodUpper = period.toUpperCase();
    switch (periodUpper) {
        case 'DAILY':
            return 'Daily';
        case 'WEEKLY':
            return 'Weekly';
        case 'MONTHLY':
            return 'Monthly';
        default:
            return period;
    }
};

const getPeriodColor = (period: string) => {
    const periodUpper = period.toUpperCase();
    switch (periodUpper) {
        case 'DAILY':
            return '#22c55e';
        case 'WEEKLY':
            return '#3b82f6';
        case 'MONTHLY':
            return '#f59e0b';
        default:
            return '#666';
    }
};

const Raffle = () => {
    const navigate = useNavigate();
    const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
    const [loading, setLoading] = useState(true);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number | ''>('');
    const [editingCode, setEditingCode] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{
        name: string;
        description: string;
        period: string;
        startDate: string;
        endDate: string;
        isActive: boolean;
        prizeType: string;
        ticketPrice: string;
        cashinFreeTicketsEnabled: boolean;
        cashinFreeTicketsPercent: string;
    }>({ 
        name: '', 
        description: '', 
        period: 'DAILY',
        startDate: '', 
        endDate: '', 
        isActive: true,
        prizeType: '',
        ticketPrice: '',
        cashinFreeTicketsEnabled: false,
        cashinFreeTicketsPercent: ''
    });

    useEffect(() => {
        const currentUser = authService.getStoredUser();
        const isAdmin = currentUser?.role?.name === 'ADMIN';
        if (isAdmin) {
            getAllEnvironmentsService.getAllEnvironments()
                .then(setEnvironments)
                .catch(console.error);
        }
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const envId = selectedEnvironmentId ? selectedEnvironmentId : undefined;
            const types = await raffleService.getTicketTypes(envId);
            setTicketTypes(types);
        } catch (error) {
            console.error('Error fetching raffle data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedEnvironmentId]);

    const findTicketByCode = (code: string): Ticket | undefined => {
        for (const group of ticketTypes) {
            const ticket = group.tickets.find((t: Ticket) => t.code === code);
            if (ticket) return ticket;
        }
        return undefined;
    };

    const handleEditClick = (ticket: Ticket) => {
        setEditingCode(ticket.code);
        
        // Parse meta to get all ticket data
        let meta: any = {};
        if (ticket.meta) {
            try {
                meta = typeof ticket.meta === 'string' ? JSON.parse(ticket.meta) : ticket.meta;
            } catch (e) {
                console.error('Error parsing meta:', e);
            }
        }

        // Format dates
        const startDate = meta.startDate ? new Date(meta.startDate) : new Date();
        const startDateTime = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
            
        const endDate = new Date(ticket.expiresAt);
        const endDateTime = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        
        setEditForm({
            name: ticket.name,
            description: meta.description || '',
            period: meta.period || 'DAILY',
            startDate: startDateTime,
            endDate: endDateTime,
            isActive: meta.isActive !== undefined ? meta.isActive : true,
            prizeType: meta.prizeType || '',
            ticketPrice: meta.ticketPrice?.toString() || '',
            cashinFreeTicketsEnabled: meta.cashinFreeTicketsEnabled || false,
            cashinFreeTicketsPercent: meta.cashinFreeTicketsPercent?.toString() || '',
        });
    };

    const handleCancelEdit = () => {
        setEditingCode(null);
        setEditForm({ 
            name: '', 
            description: '', 
            period: 'DAILY',
            startDate: '', 
            endDate: '', 
            isActive: true,
            prizeType: '',
            ticketPrice: '',
            cashinFreeTicketsEnabled: false,
            cashinFreeTicketsPercent: ''
        });
    };

    const handleSaveEdit = async (ticket: Ticket) => {
        try {
            const ticketIdNum = parseInt(ticket.id);
            if (isNaN(ticketIdNum)) {
                alert('Invalid ticket ID');
                return;
            }

            const ticketPrice = parseFloat(editForm.ticketPrice);
            if (editForm.ticketPrice && (isNaN(ticketPrice) || ticketPrice <= 0)) {
                alert('Please enter a valid ticket price');
                return;
            }

            const input: UpdateTicketBonusInput = {
                id: ticketIdNum,
                name: editForm.name,
                description: editForm.description,
                period: editForm.period as any,
                startDate: new Date(editForm.startDate).toISOString(),
                endDate: new Date(editForm.endDate).toISOString(),
                isActive: editForm.isActive,
                conditions: {
                    prizeType: editForm.prizeType,
                    ticketPrice: ticketPrice,
                    cashinFreeTicketsEnabled: editForm.cashinFreeTicketsEnabled,
                },
            };

            // Add cashinFreeTicketsPercent if enabled and has value
            if (editForm.cashinFreeTicketsEnabled && editForm.cashinFreeTicketsPercent) {
                const percent = parseFloat(editForm.cashinFreeTicketsPercent);
                if (!isNaN(percent) && percent > 0) {
                    input.conditions!.cashinFreeTicketsPercent = percent;
                }
            }

            await raffleService.updateTicketBonus(input);
            
            // Refresh data
            await fetchData();
            handleCancelEdit();
        } catch (error) {
            console.error('Error updating raffle ticket:', error);
            alert('Failed to update raffle ticket');
        }
    };

    const handleDelete = async (ticket: Ticket) => {
        const ticketIdNum = parseInt(ticket.id);
        if (isNaN(ticketIdNum)) {
            alert('Invalid ticket ID');
            return;
        }

        if (!window.confirm('Are you sure you want to deactivate this raffle ticket?')) {
            return;
        }

        try {
            await raffleService.updateTicketBonus({ id: ticketIdNum, isActive: false });
            await fetchData();
        } catch (error) {
            console.error('Error deactivating ticket:', error);
            alert('Failed to deactivate raffle ticket');
        }
    };

    const isExpired = (expiresAt: string) => {
        return new Date(expiresAt) < new Date();
    };

    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <h1 className={styles.title}>Raffle Ticket Bonuses</h1>
                <div className={styles.headerActions}>
                    {environments.length > 0 && (
                        <select
                            className={styles.formSelect}
                            value={selectedEnvironmentId}
                            onChange={(e) => setSelectedEnvironmentId(e.target.value ? Number(e.target.value) : '')}
                        >
                            <option value="">All Environments</option>
                            {environments.map((environment) => (
                                <option key={environment.id} value={environment.id}>
                                    {environment.name} - {environment.host}
                                </option>
                            ))}
                        </select>
                    )}
                    <Link to="/raffle/create" className={styles.addBtn}>
                        + Create Raffle Ticket
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className={styles.loading}>Loading raffle tickets...</div>
            ) : ticketTypes.length === 0 ? (
                <div className={styles.noData}>No raffle tickets found</div>
            ) : (
                <div className={styles.raffleGrid}>
                    {ticketTypes.map((ticketType) => (
                        ticketType.tickets.map((ticket) => (
                            <div
                                key={ticket.id}
                                className={`${styles.raffleCard} ${editingCode !== ticket.code ? styles.clickable : ''} ${isExpired(ticket.expiresAt) ? styles.expired : ''}`}
                                onClick={() => {
                                    if (editingCode !== ticket.code) {
                                        navigate(`/raffle/${ticket.id}`);
                                    }
                                }}
                            >
                                <div className={styles.cardHeader}>
                                    <div>
                                        <div className={styles.raffleName}>{ticket.name}</div>
                                        <div className={styles.raffleCode}>{ticket.code}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {editingCode === ticket.code ? (
                                            <>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleSaveEdit(ticket); }}
                                                    className={styles.saveBtn}
                                                    title="Save changes"
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                                                    className={styles.cancelEditBtn}
                                                    title="Cancel editing"
                                                >
                                                    ✕
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/raffle/${ticket.id}`); }}
                                                    className={styles.viewBtn}
                                                    title="View participants & winner"
                                                >
                                                    👁
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleEditClick(ticket); }}
                                                    className={styles.editBtn}
                                                    title="Edit raffle ticket"
                                                >
                                                    ✎
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(ticket); }}
                                                    className={styles.deleteBtn}
                                                    title="Delete raffle ticket"
                                                >
                                                    ✕
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.cardBody}>
                                    {editingCode === ticket.code ? (
                                        <div className={styles.editFormSection}>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>Name *</label>
                                                <input
                                                    type="text"
                                                    value={editForm.name}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                                    className={styles.editInput}
                                                    required
                                                />
                                            </div>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>Description *</label>
                                                <textarea
                                                    value={editForm.description}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                                    className={styles.editInput}
                                                    rows={2}
                                                    required
                                                />
                                            </div>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>Period *</label>
                                                <select
                                                    value={editForm.period}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, period: e.target.value }))}
                                                    className={styles.editInput}
                                                    required
                                                >
                                                    <option value="DAILY">Daily</option>
                                                    <option value="WEEKLY">Weekly</option>
                                                    <option value="MONTHLY">Monthly</option>
                                                </select>
                                            </div>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>Prize Type *</label>
                                                <input
                                                    type="text"
                                                    value={editForm.prizeType}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, prizeType: e.target.value.toUpperCase() }))}
                                                    className={styles.editInput}
                                                    placeholder="e.g., CAR, PHONE, USD"
                                                    required
                                                />
                                            </div>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>Ticket Price *</label>
                                                <input
                                                    type="number"
                                                    value={editForm.ticketPrice}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, ticketPrice: e.target.value }))}
                                                    className={styles.editInput}
                                                    step="0.01"
                                                    min="0"
                                                    required
                                                />
                                            </div>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.cashinFreeTicketsEnabled}
                                                        onChange={(e) => setEditForm(prev => ({ ...prev, cashinFreeTicketsEnabled: e.target.checked }))}
                                                    />
                                                    {' '}Cashin Free Tickets Enabled
                                                </label>
                                            </div>
                                            {editForm.cashinFreeTicketsEnabled && (
                                                <div className={styles.editFormGroup}>
                                                    <label className={styles.editLabel}>Cashin Free Tickets %</label>
                                                    <input
                                                        type="number"
                                                        value={editForm.cashinFreeTicketsPercent}
                                                        onChange={(e) => setEditForm(prev => ({ ...prev, cashinFreeTicketsPercent: e.target.value }))}
                                                        className={styles.editInput}
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="Optional"
                                                    />
                                                </div>
                                            )}
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>Start Date *</label>
                                                <input
                                                    type="datetime-local"
                                                    value={editForm.startDate}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, startDate: e.target.value }))}
                                                    className={styles.editInput}
                                                    required
                                                />
                                            </div>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>End Date *</label>
                                                <input
                                                    type="datetime-local"
                                                    value={editForm.endDate}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, endDate: e.target.value }))}
                                                    className={styles.editInput}
                                                    required
                                                />
                                            </div>
                                            <div className={styles.editFormGroup}>
                                                <label className={styles.editLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isActive}
                                                        onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                                    />
                                                    {' '}Active
                                                </label>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {(() => {
                                                // Parse meta for display
                                                let meta: any = {};
                                                if (ticket.meta) {
                                                    try {
                                                        meta = typeof ticket.meta === 'string' ? JSON.parse(ticket.meta) : ticket.meta;
                                                    } catch (e) {
                                                        console.error('Error parsing meta:', e);
                                                    }
                                                }
                                                
                                                return (
                                                    <>
                                                        <div className={styles.infoRow}>
                                                            <span className={styles.infoLabel}>Name</span>
                                                            <span className={styles.infoValue}>{ticket.name}</span>
                                                        </div>

                                                        {meta.description && (
                                                            <div className={styles.descriptionSection}>
                                                                <div className={styles.descriptionLabel}>Description</div>
                                                                <div className={styles.descriptionValue}>{meta.description}</div>
                                                            </div>
                                                        )}

                                                        <div className={styles.infoRow}>
                                                            <span className={styles.infoLabel}>Period</span>
                                                            <span 
                                                                className={styles.typeChip}
                                                                style={{ backgroundColor: getPeriodColor(meta.period || ticketType.period) }}
                                                            >
                                                                {getPeriodLabel(meta.period || ticketType.period)}
                                                            </span>
                                                        </div>

                                                        {meta.prizeType && (
                                                            <div className={styles.infoRow}>
                                                                <span className={styles.infoLabel}>Prize Type</span>
                                                                <span className={styles.infoValue}>{meta.prizeType}</span>
                                                            </div>
                                                        )}

                                                        {meta.ticketPrice && (
                                                            <div className={styles.infoRow}>
                                                                <span className={styles.infoLabel}>Ticket Price</span>
                                                                <span className={styles.infoValue}>${meta.ticketPrice}</span>
                                                            </div>
                                                        )}

                                                        <div className={styles.infoRow}>
                                                            <span className={styles.infoLabel}>Cashin Free Tickets</span>
                                                            <span className={styles.infoValue}>
                                                                {meta.cashinFreeTicketsEnabled ? 'Enabled' : 'Disabled'}
                                                                {meta.cashinFreeTicketsEnabled && meta.cashinFreeTicketsPercent && 
                                                                    ` (${meta.cashinFreeTicketsPercent}%)`
                                                                }
                                                            </span>
                                                        </div>

                                                        {meta.startDate && (
                                                            <div className={styles.infoRow}>
                                                                <span className={styles.infoLabel}>Start Date</span>
                                                                <span className={styles.infoValue}>{formatDate(meta.startDate)}</span>
                                                            </div>
                                                        )}

                                                        <div className={styles.infoRow}>
                                                            <span className={styles.infoLabel}>End Date</span>
                                                            <span className={`${styles.infoValue} ${isExpired(ticket.expiresAt) ? styles.expiredText : ''}`}>
                                                                {formatDate(ticket.expiresAt)}
                                                            </span>
                                                        </div>

                                                        <div className={styles.infoRow}>
                                                            <span className={styles.infoLabel}>Status</span>
                                                            <span className={styles.infoValue}>
                                                                {meta.isActive !== undefined 
                                                                    ? (meta.isActive ? '✓ Active' : '✗ Inactive')
                                                                    : '✓ Active'
                                                                }
                                                            </span>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))
                    ))}
                </div>
            )}
        </main>
    );
};

export default Raffle;